import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button, Card, Input, Select, Textarea } from "@nutrayan/ui";
import { apiClient } from "@/lib/api-client";

// Contract: POST /admin/api/v1/catalog/products (CreateProductInput),
// POST .../products/{id}/variants (CreateVariantInput), POST .../products/{id}/publish.

const productSchema = z.object({
  slug: z
    .string()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "kebab-case only (a-z, 0-9, hyphens)")
    .max(100),
  name: z.string().min(2).max(200),
  line: z.enum(["SHAKTI", "SATTVA", "AMRITA", "AGNI", "TRIAL"]),
  shortDescription: z.string().min(10, "At least 10 characters"),
  longDescription: z.string().optional(),
  classicalReference: z.string().optional(),
  subscribable: z.boolean(),
  trialPack: z.boolean(),
});
type ProductForm = z.infer<typeof productSchema>;

const variantSchema = z.object({
  sku: z.string().regex(/^[A-Z]{3}-[A-Z]{3}-\d{3}(-[A-Z0-9]+)?$/, "Format: NUT-SHK-001"),
  name: z.string().min(2),
  priceMrpPaise: z.coerce.number().int().positive("MRP in paise, e.g. 129900"),
  priceSellingPaise: z.coerce.number().int().positive(),
  priceSubscriptionPaise: z.coerce.number().int().positive(),
  servingsCount: z.coerce.number().int().positive(),
});
type VariantForm = z.infer<typeof variantSchema>;

interface ProductView {
  id: number;
  slug?: string;
  name?: string;
  status?: string;
  variants?: Array<{ id: number; sku?: string; name?: string }>;
}

export default function ProductEditorPage() {
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductView | null>(null);
  const [variants, setVariants] = useState<Array<{ id: number; sku?: string; name?: string }>>([]);

  const productForm = useForm<ProductForm>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      slug: "",
      name: "",
      line: "SHAKTI",
      shortDescription: "",
      longDescription: "",
      classicalReference: "",
      subscribable: true,
      trialPack: false,
    },
  });

  const variantForm = useForm<VariantForm>({
    resolver: zodResolver(variantSchema),
    defaultValues: {
      sku: "",
      name: "",
      priceMrpPaise: 0,
      priceSellingPaise: 0,
      priceSubscriptionPaise: 0,
      servingsCount: 60,
    },
  });

  const createProduct = useMutation({
    mutationFn: (data: ProductForm) =>
      apiClient.request<ProductView>("/admin/api/v1/catalog/products", {
        method: "POST",
        body: data,
      }),
    onSuccess: (p) => {
      setProduct(p);
      toast.success(`Draft created: ${p.name}`);
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't create product"),
  });

  const addVariant = useMutation({
    mutationFn: (data: VariantForm) =>
      apiClient.request<{ id: number; sku?: string; name?: string }>(
        `/admin/api/v1/catalog/products/${product?.id}/variants`,
        { method: "POST", body: data },
      ),
    onSuccess: (v) => {
      setVariants((prev) => [...prev, v]);
      variantForm.reset();
      toast.success(`Variant added: ${v.sku}`);
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't add variant"),
  });

  const publish = useMutation({
    mutationFn: () =>
      apiClient.request<void>(`/admin/api/v1/catalog/products/${product?.id}/publish`, {
        method: "POST",
      }),
    onSuccess: () => {
      toast.success("Published");
      navigate("/catalog");
    },
    onError: (e: Error) =>
      // Publish gate: requires variant + image + lab report on the backend.
      toast.error(e.message || "Publish gate failed — variant, image, and lab report required"),
  });

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">New product</h1>
        <p className="text-body text-ink">
          Create a draft, add variants, then publish (gate: variant + image + lab report).
        </p>
      </header>

      <Card>
        <h2 className="font-display text-display-sm text-charcoal">1. Product draft</h2>
        <form
          className="mt-4 grid gap-3 sm:grid-cols-2"
          onSubmit={productForm.handleSubmit((d) => createProduct.mutate(d))}
        >
          <div>
            <Input
              placeholder="Name"
              disabled={!!product}
              {...productForm.register("name")}
              invalid={!!productForm.formState.errors.name}
            />
          </div>
          <div>
            <Input
              placeholder="slug-like-this"
              disabled={!!product}
              {...productForm.register("slug")}
              invalid={!!productForm.formState.errors.slug}
            />
            {productForm.formState.errors.slug ? (
              <p className="text-caption text-terracotta mt-1">
                {productForm.formState.errors.slug.message}
              </p>
            ) : null}
          </div>
          <Select disabled={!!product} {...productForm.register("line")}>
            {["SHAKTI", "SATTVA", "AMRITA", "AGNI", "TRIAL"].map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </Select>
          <Input
            placeholder="Classical reference (optional)"
            disabled={!!product}
            {...productForm.register("classicalReference")}
          />
          <div className="sm:col-span-2">
            <Textarea
              placeholder="Short description (storefront card + PDP intro)"
              disabled={!!product}
              {...productForm.register("shortDescription")}
              invalid={!!productForm.formState.errors.shortDescription}
            />
          </div>
          <div className="sm:col-span-2">
            <Textarea
              placeholder="Long description (optional)"
              disabled={!!product}
              {...productForm.register("longDescription")}
            />
          </div>
          <label className="text-body-sm text-ink flex items-center gap-2">
            <input type="checkbox" disabled={!!product} {...productForm.register("subscribable")} />
            Subscribable (Subscribe &amp; Save)
          </label>
          <label className="text-body-sm text-ink flex items-center gap-2">
            <input type="checkbox" disabled={!!product} {...productForm.register("trialPack")} />
            Trial pack
          </label>
          {!product ? (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={createProduct.isPending}>
                {createProduct.isPending ? "Creating…" : "Create draft"}
              </Button>
            </div>
          ) : (
            <p className="text-body-sm text-forest sm:col-span-2">✓ Draft #{product.id} created</p>
          )}
        </form>
      </Card>

      {product ? (
        <Card>
          <h2 className="font-display text-display-sm text-charcoal">2. Variants</h2>
          {variants.length ? (
            <ul className="text-body-sm text-ink mt-2 list-disc pl-5">
              {variants.map((v) => (
                <li key={v.id}>
                  {v.sku} — {v.name}
                </li>
              ))}
            </ul>
          ) : null}
          <form
            className="mt-4 grid gap-3 sm:grid-cols-2"
            onSubmit={variantForm.handleSubmit((d) => addVariant.mutate(d))}
          >
            <div>
              <Input
                placeholder="SKU (NUT-SHK-002)"
                {...variantForm.register("sku")}
                invalid={!!variantForm.formState.errors.sku}
              />
              {variantForm.formState.errors.sku ? (
                <p className="text-caption text-terracotta mt-1">
                  {variantForm.formState.errors.sku.message}
                </p>
              ) : null}
            </div>
            <Input
              placeholder="Variant name (60 caps)"
              {...variantForm.register("name")}
              invalid={!!variantForm.formState.errors.name}
            />
            <Input
              type="number"
              placeholder="MRP (paise)"
              {...variantForm.register("priceMrpPaise")}
              invalid={!!variantForm.formState.errors.priceMrpPaise}
            />
            <Input
              type="number"
              placeholder="Selling price (paise)"
              {...variantForm.register("priceSellingPaise")}
              invalid={!!variantForm.formState.errors.priceSellingPaise}
            />
            <Input
              type="number"
              placeholder="Subscription price (paise)"
              {...variantForm.register("priceSubscriptionPaise")}
              invalid={!!variantForm.formState.errors.priceSubscriptionPaise}
            />
            <Input
              type="number"
              placeholder="Servings"
              {...variantForm.register("servingsCount")}
              invalid={!!variantForm.formState.errors.servingsCount}
            />
            <div className="sm:col-span-2">
              <Button type="submit" variant="outline" disabled={addVariant.isPending}>
                {addVariant.isPending ? "Adding…" : "+ Add variant"}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {product ? (
        <Card>
          <h2 className="font-display text-display-sm text-charcoal">3. Publish</h2>
          <p className="text-body-sm text-ink mt-2">
            The backend enforces the publish gate: at least one variant, one image, and a published
            lab report batch. Media + lab reports attach via the batch workflow.
          </p>
          <Button
            className="mt-3"
            onClick={() => publish.mutate()}
            disabled={publish.isPending || variants.length === 0}
          >
            {publish.isPending ? "Publishing…" : "Publish product"}
          </Button>
        </Card>
      ) : null}
    </div>
  );
}
